import { PAYMENT_METHOD, isPaymentPending } from './utilityFunctions';

/**
 * Builds the props for `ShopModalOrderComplete` after an academy purchase
 * (journey, course or session). The three detail pages used to carry a copy of
 * this each; they now share it so the branches cannot drift apart.
 *
 * Three outcomes:
 *  - unlocked — the content is available right away, so the modal offers a
 *    single button that reloads the page to reveal it;
 *  - pending payment — the order is created but settled outside the checkout
 *    (BRAVON-6460). Navigating to the journeys list or to order tracking helps
 *    nobody while the order simply waits, so the modal offers a single button
 *    that closes it;
 *  - anything else — unchanged: "back to list" plus "track your order".
 *
 * `isPendingPayment` is reported back so the page underneath can show the
 * waiting state the moment the confirmation opens, without re-deriving the
 * branch and risking a page that disagrees with the pop-up standing over it.
 */
// eslint-disable-next-line import/prefer-default-export
export const buildAcademySuccessModalData = ({
  t,
  itemKey,
  paymentMethod,
  total,
  redirectStatus,
  contentAvailableBeforePayment,
  urlGoBack,
  urlGoToOrder,
  onReload,
  onClose,
  message,
}) => {
  const itemLabel = t(itemKey);

  const isUnlocked =
    paymentMethod === PAYMENT_METHOD.CREDIT_CARD ||
    paymentMethod === PAYMENT_METHOD.BANCONTACT ||
    paymentMethod === PAYMENT_METHOD.PAYPAL ||
    redirectStatus === 'succeeded' ||
    contentAvailableBeforePayment;

  if (isUnlocked) {
    return {
      urlGoBack: null,
      urlGoToOrder: null,
      modalMessage: message || t('academy.modal.success.order', itemLabel),
      onCloseModal: onReload,
      buttonMessage: t('ok'),
      showBack: false,
      isPendingPayment: false,
    };
  }

  // The order status cannot be used here: the API reports `pending_payment` for
  // every order the moment it is created, successful card payments included.
  if (isPaymentPending(paymentMethod, total)) {
    // `message` is deliberately NOT honoured here. Its only caller is the
    // session page's "bought for other people" line, and when the payment is
    // still outstanding what happens next matters more than who was invited —
    // the same rule the kit applies in web_user_portal_v2.
    return {
      urlGoBack: null,
      urlGoToOrder: null,
      modalMessage: t('academy.modal.success.pendingPayment', itemLabel),
      onCloseModal: onClose,
      buttonMessage: t('ok'),
      showBack: false,
      isPendingPayment: true,
    };
  }

  return {
    urlGoBack,
    urlGoToOrder,
    modalMessage: message || t('academy.modal.success.bankTransfer', itemLabel),
    onCloseModal: onClose,
    buttonMessage: null,
    showBack: true,
    isPendingPayment: false,
  };
};
